import { useSelector } from "react-redux";

/**
 * Computes role-based access flags for investigation pages.
 *
 * @param {object|null} investigation - The investigation record (from API)
 * @param {boolean} isSafetyAdmin - Whether the current user is a Safety Admin
 * @returns {{ canEdit, canSubmit, isViewOnly, isApproved, isSubmitted, isInProgress, isAssignedInvestigator }}
 */
const useInvestigationAccess = (investigation, isSafetyAdmin) => {
  const currentUserId = useSelector((state) => state.user?.user?.id);
  const normalizedCurrentUserId =
    typeof currentUserId === "number" ? currentUserId : Number(currentUserId);

  const isAssignedInvestigator =
    investigation &&
    Number(investigation.investigator_id) === normalizedCurrentUserId;

  const isApproved = investigation?.status === "approved";
  const isSubmitted = investigation?.status === "submitted";
  const isInProgress =
    !investigation ||
    investigation.status === "in_progress" ||
    investigation.status === "more_info_requested";

  // Safety Admin: can edit any time except when approved
  // Assigned Investigator: can edit when in_progress or more_info_requested
  // Everyone else: view-only
  const canEdit = isSafetyAdmin
    ? !isApproved
    : isAssignedInvestigator
      ? isInProgress
      : false;

  const canSubmit =
    (isSafetyAdmin || isAssignedInvestigator) && !isApproved && !isSubmitted;

  const isViewOnly = !isSafetyAdmin && !isAssignedInvestigator;

  return {
    canEdit,
    canSubmit,
    isViewOnly,
    isApproved,
    isSubmitted,
    isInProgress,
    isAssignedInvestigator,
  };
};

export default useInvestigationAccess;
